import React, { useState, useEffect } from "react";
import { Button } from "@telegram-apps/telegram-ui";
import type { PriceMode } from "./priceSource";
import { TemporaryCoinsService } from "../../services/TemporaryCoinsService"; // Import temporary coins service
import "./PreGameModal.css";

interface PreGameModalProps {
  onStartGame: (startingAmount: number, mode: PriceMode) => void;
  onCancel: () => void;
}

interface CoinOption {
  amount: number;
  label: string;
  description: string;
  recommended?: boolean;
}

const COIN_OPTIONS: CoinOption[] = [
  {
    amount: 100,
    label: "100 dollars",
    description: "Conservative start",
  },
  {
    amount: 1000,
    label: "1,000 dollars",
    description: "Balanced approach",
  },
  {
    amount: 10000,
    label: "10,000 dollars",
    description: "High roller mode",
  },
];

export function PreGameModal({ onStartGame, onCancel }: PreGameModalProps) {
  const [selectedAmount, setSelectedAmount] = useState(1000); // Default to 1000
  const [mode, setMode] = useState<PriceMode>("real");
  // TEMPORARY: Track current temporary coins balance
  const [availableCoins, setAvailableCoins] = useState(
    TemporaryCoinsService.getCoins()
  );

  // Update available coins when component mounts
  useEffect(() => {
    setAvailableCoins(TemporaryCoinsService.getCoins());
  }, []);

  const handleStartGame = () => {
    // TEMPORARY: Check if user has enough temporary coins
    if (availableCoins < selectedAmount) {
      alert(
        `Insufficient coins! You have ${availableCoins} coins but need ${selectedAmount}.`
      );
      return;
    }

    onStartGame(selectedAmount, mode);
  };

  // Check if user can afford the selected amount
  const canAfford = availableCoins >= selectedAmount;

  return (
    <div className="pregame-overlay">
      <div className="pregame-modal">
        <div className="pregame-header">
          <h2 className="pregame-title">🎮 Select Mode & Starting Capital</h2>

          {/* TEMPORARY: Show current coin balance */}
          <div className="current-balance">
            💰 Available:{" "}
            <strong>{availableCoins.toLocaleString()} dollars</strong>
          </div>
        </div>

        <div className="mode-options">
          <Button
            size="s"
            onClick={() => setMode("random")}
            className={`mode-btn ${
              mode === "random" ? "mode-btn--active" : ""
            }`}
          >
            🎲 Random
          </Button>
          <Button
            size="s"
            onClick={() => setMode("real")}
            className={`mode-btn ${mode === "real" ? "mode-btn--active" : ""}`}
          >
            📈 Real coins
          </Button>
          <Button
            size="s"
            onClick={() => setMode("pvp")}
            className={`mode-btn ${mode === "pvp" ? "mode-btn--active" : ""}`}
          >
            ⚔️ PvP (vs bot)
          </Button>
        </div>

        <div className="coin-options">
          {COIN_OPTIONS.map((option) => {
            const isAffordable = availableCoins >= option.amount;
            return (
              <div
                key={option.amount}
                className={`coin-option ${
                  selectedAmount === option.amount ? "selected" : ""
                } ${option.recommended ? "recommended" : ""} ${
                  !isAffordable ? "unaffordable" : ""
                }`}
                onClick={() => isAffordable && setSelectedAmount(option.amount)}
                style={{
                  opacity: isAffordable ? 1 : 0.5,
                  cursor: isAffordable ? "pointer" : "not-allowed",
                }}
              >
                <div className="coin-amount">{option.label}</div>
                <div className="coin-conversion">
                  = ${option.amount.toLocaleString()}
                </div>

                <div className="selection-indicator">
                  {selectedAmount === option.amount && isAffordable && (
                    <span>✓</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="pregame-actions">
          <Button
            size="l"
            onClick={handleStartGame}
            className={`start-trading-button ${!canAfford ? "disabled" : ""}`}
            disabled={!canAfford}
          >
            {canAfford
              ? `🚀 Start Trading`
              : `❌ Insufficient Coins (need ${selectedAmount.toLocaleString()})`}
          </Button>

          <Button size="l" onClick={onCancel} className="cancel-button">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
